import React, { useState } from 'react';

/**
 * ContactPage — Standard contact/support page.
 * Displays contact form and support information.
 */
const ContactPage = ({ onBack }) => {
    const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Open mailto with pre-filled fields
        const mailtoUrl = `mailto:support@minutemarket.com?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(
            `From: ${form.name} (${form.email})\n\n${form.message}`
        )}`;
        window.open(mailtoUrl, '_blank');
        setSubmitted(true);
    };

    return (
        <div className="flex items-center justify-center min-h-[calc(100vh-50px)] px-4">
            <div className="w-full max-w-lg">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-sm overflow-hidden">
                    {/* Header */}
                    <div className="px-8 pt-8 pb-4 border-b border-white/5">
                        <h1 className="text-2xl font-bold text-white tracking-wide mb-1">Contact Support</h1>
                        <p className="text-sm text-gray-400">
                            Have a question or issue? We're here to help.
                        </p>
                    </div>

                    {submitted ? (
                        <div className="px-8 py-12 text-center">
                            <div className="text-4xl mb-4">✅</div>
                            <h2 className="text-lg font-bold text-white mb-2">Message Ready</h2>
                            <p className="text-sm text-gray-400 mb-6">
                                Your email client should have opened with the message pre-filled. Send it and we'll respond within 24 hours.
                            </p>
                            <button
                                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                                className="px-6 py-2 text-sm text-cyan-400 border border-cyan-500/30 rounded hover:bg-cyan-500/10 transition-all uppercase tracking-wider font-bold"
                            >
                                Send Another
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Name</label>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 transition-colors"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 transition-colors"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Subject</label>
                                <select
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    required
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 transition-colors appearance-none"
                                >
                                    <option value="" className="bg-[#0c0c1a]">Select a topic...</option>
                                    <option value="Deposit Issue" className="bg-[#0c0c1a]">Deposit Issue</option>
                                    <option value="Withdrawal Issue" className="bg-[#0c0c1a]">Withdrawal Issue</option>
                                    <option value="Account Problem" className="bg-[#0c0c1a]">Account Problem</option>
                                    <option value="Bug Report" className="bg-[#0c0c1a]">Bug Report</option>
                                    <option value="Feature Request" className="bg-[#0c0c1a]">Feature Request</option>
                                    <option value="General Inquiry" className="bg-[#0c0c1a]">General Inquiry</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Message</label>
                                <textarea
                                    required
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    rows={4}
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
                                    placeholder="Describe your issue or question..."
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 rounded font-bold text-sm uppercase tracking-wider hover:bg-cyan-500/30 transition-all"
                            >
                                Send Message
                            </button>
                        </form>
                    )}

                    {/* Footer info */}
                    <div className="px-8 py-4 border-t border-white/5 bg-white/[0.01]">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>✉ support@minutemarket.com</span>
                            <span>Response within 24h</span>
                        </div>
                    </div>
                </div>

                {/* Back link */}
                <button
                    onClick={onBack}
                    className="mt-4 text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-2 mx-auto"
                >
                    ← Back to Trading
                </button>
            </div>
        </div>
    );
};

export default ContactPage;
